import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import type { IPost } from 'types/interfaces/ITypes'

type PreviewLocation =
    | 'home'
    | 'profile'
    | 'purchased'
    | 'watched'
    | 'liked'
    | 'playlist'
    | ''

export interface IPreviewStateCore {
    selectedPost: number | null
    selectedSlide: number | null
    selectedPostType: string
    userId: string | null
    filter: string
    query: readonly any[]
}

export interface IFilterQuery {
    type: string
    media_category?: string
    listId?: string
}

function derivePreviewLocation(
    dataQuery: readonly any[] | undefined | null
): PreviewLocation {
    if (!Array.isArray(dataQuery) || dataQuery.length === 0) return ''

    const key = dataQuery[0]
    const k = typeof key === 'string' ? key : String(key)

    switch (k) {
        case 'getHomeFeed':
            return 'home'

        case 'getProfileFeed':
        case 'getFeed':
        case 'getCreatorFeed':
        case 'getUserFeed':
            return 'profile'

        case 'getPurchasedMedia':
        case 'getPurchases':
            return 'purchased'

        case 'getWatchHistory':
        case 'getWatchedMedia':
            return 'watched'

        case 'getLikedMedia':
        case 'getLikes':
            return 'liked'

        case 'getPlaylist':
        case 'getPlaylists':
            return 'playlist'

        default: {
            const s = k.toLowerCase()

            if (s.includes('home')) return 'home'
            if (s.includes('profile')) return 'profile'
            if (s.includes('purch')) return 'purchased'
            if (s.includes('watch')) return 'watched'
            if (s.includes('like')) return 'liked'
            if (s.includes('play')) return 'playlist'

            return ''
        }
    }
}

type PreviewStore = {
    previewOpen: boolean
    minimized: boolean
    commentsActive: boolean

    state: IPreviewStateCore
    filterQuery: IFilterQuery | {}
    selectedPostData: IPost | null
    previewLocation: PreviewLocation
    commentCountByPostId: Record<string, number>
    lastLocation: PreviewLocation

    setPreviewOpen: (v: boolean) => void
    setMinimized: (v: boolean) => void
    setCommentsActive: (v: boolean) => void
    setSelectedPostData: (p: IPost | null) => void
    setHomeFeedActivePost: (p: IPost, postIndex: number) => void
    setFilterQuery: (q: IFilterQuery | {}) => void
    setState: (fn: (prev: IPreviewStateCore) => IPreviewStateCore) => void

    setPreviewLocationFromQuery: (
        dataQuery: readonly any[] | undefined | null
    ) => void

    setPreviewLocation: (loc: PreviewLocation) => void

    setPostCommentCount: (
        postId: string,
        count: number
    ) => void

    bumpPostCommentCount: (
        postId: string,
        delta: number
    ) => void

    bumpSelectedPostCommentCount: (
        delta: number
    ) => void

    addModal: (
        postNumber: number,
        selectedPostSlide: number,
        type: string,
        postData: IPost,
        dataQuery: readonly any[]
    ) => void

    clearModal: () => void

    updateSelectedPost: (
        postIdx: number,
        slideIdx: number,
        type: string,
        postData: IPost,
        dataQuery?: readonly any[]
    ) => void

    setInitialData: (
        userId: string,
        filter: string,
        location: string,
        filterQ?: IFilterQuery | {},
        query?: any[],
        newState?: Partial<IPreviewStateCore>
    ) => void

    setFilter: (filter: string) => void

    setFilters: (
        filter: string,
        category: string
    ) => void

    handleMinimize: (
        isMinimized: boolean
    ) => void

    resetPreviewState: () => void
}

const initialCore: IPreviewStateCore = {
    selectedPost: null,
    selectedSlide: null,
    selectedPostType: '',
    userId: null,
    filter: '',
    query: []
}

const isLocal = true

const storeImpl = (set: any, get: any): PreviewStore => ({
    previewOpen: false,
    minimized: false,
    commentsActive: false,

    state: initialCore,
    filterQuery: {},
    selectedPostData: null,
    previewLocation: '',
    commentCountByPostId: {},
    lastLocation: '',

    setPreviewOpen: v =>
        set({
            previewOpen: v
        }),

    setMinimized: v =>
        set({
            minimized: v
        }),

    setCommentsActive: v =>
        set({
            commentsActive: v
        }),

    setSelectedPostData: p =>
        set({
            selectedPostData: p
        }),
    setHomeFeedActivePost: (post, postIndex) =>
    set((s: PreviewStore) => {
        const previousPost = s.selectedPostData

        const leavingStory = Boolean(
        previousPost?.stories?.length
        )

        const postChanged = previousPost?.id !== post.id

        return {
        selectedPostData: post,
        state: {
            ...s.state,
            selectedPost: postIndex,
            selectedSlide:
            postChanged && leavingStory
                ? 0
                : s.state.selectedSlide
        }
        }
    }),
    setFilterQuery: q =>
        set({
            filterQuery: q
        }),

    setState: fn =>
        set((s: PreviewStore) => ({
            state: fn(s.state)
        })),

    setPreviewLocationFromQuery: dataQuery => {
        const nextLoc = derivePreviewLocation(dataQuery)
        if (!nextLoc) return

        const cur = get().lastLocation
        if (cur === nextLoc) return

        set({
            previewLocation: nextLoc,
            lastLocation: nextLoc
        })
    },

    setPreviewLocation: loc => {
        const cur = get().lastLocation
        if (cur === loc) return

        set({
            previewLocation: loc,
            lastLocation: loc
        })
    },

    setPostCommentCount: (postId, count) => {
        set((s: PreviewStore) => ({
            commentCountByPostId: {
                ...s.commentCountByPostId,
                [String(postId)]: Math.max(0, count)
            }
        }))
    },

    bumpPostCommentCount: (postId, delta) => {
        set((s: PreviewStore) => {
            const key = String(postId)

            const current =
                s.commentCountByPostId[key] ??
                s.selectedPostData?.comment_count ??
                0

            const nextCount = Math.max(0, current + delta)

            return {
                commentCountByPostId: {
                    ...s.commentCountByPostId,
                    [key]: nextCount
                },

                selectedPostData:
                    s.selectedPostData?.id === postId
                        ? {
                              ...s.selectedPostData,
                              comment_count: nextCount
                          }
                        : s.selectedPostData
            }
        })
    },

    bumpSelectedPostCommentCount: delta => {
        const selectedPostData = get().selectedPostData
        if (!selectedPostData?.id) return

        get().bumpPostCommentCount(
            selectedPostData.id,
            delta
        )
    },

    addModal: (
        postNumber,
        selectedPostSlide,
        type,
        postData,
        dataQuery
    ) => {
        document.body.style.overflow = 'hidden'

        get().setPreviewLocationFromQuery(dataQuery)

        set((s: PreviewStore) => {
            const postId = postData?.id

            const override =
                postId != null
                    ? s.commentCountByPostId[String(postId)]
                    : undefined

            return {
                selectedPostData:
                    postId != null && override != null
                        ? {
                              ...postData,
                              comment_count: override
                          }
                        : postData,

                previewOpen: true,

                state: {
                    ...s.state,
                    selectedPost: postNumber,
                    selectedSlide: selectedPostSlide,
                    selectedPostType: type,
                    query: dataQuery
                }
            }
        })
    },

    clearModal: () => {
        document.body.style.removeProperty('overflow')

        set({
            previewOpen: false,
            minimized: false,
            commentsActive: false
        })
    },

    updateSelectedPost: (
        postIdx,
        slideIdx,
        type,
        postData,
        dataQuery
    ) => {
        if (dataQuery) {
            get().setPreviewLocationFromQuery(dataQuery)
        }

        set((s: PreviewStore) => {
            const postId = postData?.id

            const override =
                postId != null
                    ? s.commentCountByPostId[String(postId)]
                    : undefined

            return {
                selectedPostData:
                    postId != null && override != null
                        ? {
                              ...postData,
                              comment_count: override
                          }
                        : postData,

                previewOpen: true,

                state: {
                    ...s.state,
                    selectedPost: postIdx,
                    selectedSlide: slideIdx,
                    selectedPostType: type,
                    ...(dataQuery ? { query: dataQuery } : null)
                }
            }
        })
    },

    setInitialData: (
        userId,
        filter,
        location,
        filterQ,
        query,
        newState
    ) => {
        if (filterQ) {
            set({
                filterQuery: filterQ
            })
        }

        set((s: PreviewStore) => ({
            state: {
                ...s.state,
                ...(newState ?? null),
                userId,
                filter,
                ...(query ? { query } : null)
            }
        }))

        if (query?.length) {
            get().setPreviewLocationFromQuery(query)
        } else {
            const loc = (location as PreviewLocation) ?? ''
            if (loc) get().setPreviewLocation(loc)
        }
    },

    setFilter: filter =>
        set((s: PreviewStore) => ({
            state: {
                ...s.state,
                filter
            }
        })),

    setFilters: (filter, category) =>
        set({
            filterQuery: {
                type: filter,
                media_category: category
            }
        }),

    handleMinimize: isMinimized => {
        set({
            minimized: isMinimized
        })

        if (isMinimized) {
            document.body.style.removeProperty('overflow')
        } else {
            document.body.style.overflow = 'hidden'
        }
    },

    resetPreviewState: () => {
        set({
            previewOpen: false,
            minimized: false,
            commentsActive: false,
            selectedPostData: null,
            filterQuery: {},
            previewLocation: '',
            commentCountByPostId: {},
            lastLocation: '',
            state: initialCore
        })
    }
})

export const usePreviewStore = create<PreviewStore>()(
    isLocal
        ? devtools(storeImpl, { name: 'PreviewStore' })
        : storeImpl
)